import { Alert, Checkbox, Divider, FormGroup, Spacer, TextInput } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type {
    IOrthopticsRequestCustomControlDataRedenVanVerwijzing,
    OrthV1FinalRequest,
} from '@common/validation/customControls/ophthalmology';
import type { IReduxState } from '@frontend/configureStore';
import {
    CustomControlMode,
    FilesUrlBase,
    PractitionerRoleInRequest,
    ProcessEntity,
    ServiceCode,
    ToastType,
} from '@common/enums';
import { ServicePermissionsAuthorizations } from '@common/interfaces/servicePermissions';
import { orthopticsRequestCustomControlDataRedenVanVerwijzingLabels } from '@common/validation/customControls/ophthalmology';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { IMedication } from '../../../interfaces/medication';
import type { IRequestAddEdit } from '../../requests/RequestItemAddEdit';
import type { ICustomControlRequestAddEditProps } from '../interfaces/';
import { translateKey } from '../../../../translate';
import { showServiceToast } from '../../../../utils/showToast';
import { SaveButton } from '../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, HeaderDivider } from '../../../elements/common';
import { FormValidationMessage } from '../../../elements/FormValidationMessage';
import { InputText } from '../../../userDataManagement/InputText';
import { PatientInsuranceOldCarepath } from '../../episodeCategory/customControls/patientInsurance/PatientInsuranceOldCarepath';
import { ProfessionalSummaryData } from '../../professionalSummaryData/ProfessionalSummaryData';
import { OrthOFolV1FollowingSteps } from '../OrthOFol/v1/OrthOFolV1FollowingSteps';
import { AssetDropzoneWithActions } from '../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../utils/v1/CareProviderSelector';
import { MedicationsAddEdit } from '../utils/v1/medications/MedicationsAddEdit';
import { ServiceOrganizationProvider } from '../utils/v1/ServiceOrganizationProvider';
import { OrthOV1FollowingSteps } from './OrthOV1FollowingSteps';

interface IOrthopticsV1RequestAddEditProps extends ICustomControlRequestAddEditProps<OrthV1FinalRequest> {
    isOrthOFol?: boolean;
}

export function OrthopticsV1RequestAddEdit(props: IOrthopticsV1RequestAddEditProps) {
    const [isUploading, setIsUploading] = useState(false);
    const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<string>();
    const [isSaving, setIsSaving] = useState<false | 'draft' | 'final'>(false);
    const [isAndersChecked, setIsAndersChecked] = useState(
        !!props.request.customControlData.reden_van_verwijzing.andersText
    );
    const isAndersInvalid = isAndersChecked && props.request.customControlData.reden_van_verwijzing.andersText === null;
    const [redenVanVerwijzingError, setRedenVanVerwijzingError] = useState('');
    const {
        servicePermissions: { [ServiceCode.OrthO]: orthoPermissions },
    } = useSelector((state: IReduxState) => state.authentication);

    useEffect(() => {
        if (!isAndersChecked) {
            handleChangeRedenVanVerwijzing('andersText', null, false);
        }
    }, [isAndersChecked]);

    useEffect(() => {
        if (isAndersInvalid) {
            setRedenVanVerwijzingError('any_required');
        } else {
            setRedenVanVerwijzingError(props.errors.get('customControlData.reden_van_verwijzing') || '');
        }
    }, [props.errors, isAndersInvalid]);

    useEffect(() => {
        if (isSaving) {
            saveRequest(isSaving);
        }
    }, [isSaving]);

    return (
        <Form onSubmit={() => null}>
            <PatientInsuranceOldCarepath
                handleChangeCustomControl={props.handleChangeCustomControl}
                isPatientInformed={props.request.customControlData.isPatientInformed}
                mode={CustomControlMode.edit}
                onCancel={props.onCancel}
                serviceCode={props.isOrthOFol ? ServiceCode.OrthOFol : ServiceCode.OrthO}
            />
            <CareProviderSelector
                {...props}
                entity={props.request}
            />

            <ServiceOrganizationProvider
                errors={props.errors}
                initialOrganizationId={props.request.selectedProcedureProviderOrganizationId}
                initialPractitionerId={props.request.selectedProcedureProviderPractitionerId}
                onChange={async (organizationId, practitionerId) => {
                    await handleChange('selectedProcedureProviderOrganizationId', organizationId);
                    await handleChange('selectedProcedureProviderPractitionerId', practitionerId);
                }}
                onDoValidate={() => props.validateOne('selectedProcedureProviderOrganizationId')}
                roleOfPractitionersInList={PractitionerRoleInRequest.biometrician}
                serviceProviderServiceId={props.service.id}
            />

            {fileUploadErrorMessage && <Alert appearance="warning">{fileUploadErrorMessage}</Alert>}
            {orthoPermissions?.[ServicePermissionsAuthorizations.canAddAssets] && (
                <FormGroup
                    hasErrors={Boolean(fileUploadErrorMessage)}
                    isRequired={true}
                    label={translateKey('request.referralLetter')}
                >
                    <AssetDropzoneWithActions
                        acceptedFiles=".pdf"
                        assets={props.request?.customControlData.files ?? []}
                        assetsBaseUrl={FilesUrlBase.requests}
                        cleanDropzoneAfterCompletingFiles={props.isAutoSaveActive}
                        maxFiles={1}
                        onChange={async (assets) => {
                            await props.handleChangeCustomControl({ files: assets });
                        }}
                        onUploading={async (isUploading) => setIsUploading(isUploading)}
                        processActivityId={props.request.id}
                        service={props.service}
                    />
                </FormGroup>
            )}

            <Divider />

            <MedicationsAddEdit
                entity={ProcessEntity.request}
                entityId={props.request.id}
                errors={props.errors}
                handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                    await props.handleChange('patientUsesMedication', patientUsesMedication)
                }
                onChange={async (medications: IMedication[]) =>
                    await props.handleChange('requestMedications', medications)
                }
                patientUsesMedication={props.request.patientUsesMedication}
                validateMedications={async () => await props.validateOne('requestMedications')}
                validatePatientUsesMedication={async () => await props.validateOne('patientUsesMedication')}
            />

            <ProfessionalSummaryData
                entity={ProcessEntity.request}
                entityId={props.request.id}
                errors={props.errors}
                handleChangeAllergies={async (allergies) => await props.handleChange('allergies', allergies)}
                handleChangeEpisodes={async (episodesAndJournal) =>
                    await props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                }
                handleChangeLabResults={async (labResults) => await props.handleChange('requestLabResults', labResults)}
                validateEpisodesAndJournals={async () => await props.validateOne('requestEpisodesAndJournal')}
            />

            <HeaderDivider text={translateKey('request.request')} />

            <FormGroup
                hasErrors={props.errors.has('customControlData.reden_van_verwijzing')}
                isRequired={true}
                label="Reden van verwijzing"
            >
                <Spacer
                    lastChildHasNoSpacing
                    spacing="small"
                >
                    <Checkbox
                        checked={
                            props.request.customControlData.reden_van_verwijzing
                                .geleidelijke_daling_gezichtsscherpte_bij_kind
                        }
                        label={
                            // eslint-disable-next-line @stylistic/max-len
                            orthopticsRequestCustomControlDataRedenVanVerwijzingLabels.geleidelijke_daling_gezichtsscherpte_bij_kind
                        }
                        onChange={async (event) => {
                            await handleChangeRedenVanVerwijzing(
                                'geleidelijke_daling_gezichtsscherpte_bij_kind',
                                event.target.checked
                            );
                        }}
                    />
                    <Checkbox
                        checked={props.request.customControlData.reden_van_verwijzing.scheelzien_altijd_geleidelijk}
                        label={orthopticsRequestCustomControlDataRedenVanVerwijzingLabels.scheelzien_altijd_geleidelijk}
                        onChange={async (event) => {
                            await handleChangeRedenVanVerwijzing('scheelzien_altijd_geleidelijk', event.target.checked);
                        }}
                    />
                    <Checkbox
                        checked={props.request.customControlData.reden_van_verwijzing.hoofdpijn}
                        label={orthopticsRequestCustomControlDataRedenVanVerwijzingLabels.hoofdpijn}
                        onChange={async (event) => {
                            await handleChangeRedenVanVerwijzing('hoofdpijn', event.target.checked);
                        }}
                    />
                    <Checkbox
                        checked={
                            props.request.customControlData.reden_van_verwijzing
                                .geleidelijk_ontstaan_langer_bestaand_binoculair_dubbelzien
                        }
                        label={
                            // eslint-disable-next-line @stylistic/max-len
                            orthopticsRequestCustomControlDataRedenVanVerwijzingLabels.geleidelijk_ontstaan_langer_bestaand_binoculair_dubbelzien
                        }
                        onChange={async (event) => {
                            await handleChangeRedenVanVerwijzing(
                                'geleidelijk_ontstaan_langer_bestaand_binoculair_dubbelzien',
                                event.target.checked
                            );
                        }}
                    />
                    <Checkbox
                        checked={props.request.customControlData.reden_van_verwijzing.asthenope_klachten}
                        label={orthopticsRequestCustomControlDataRedenVanVerwijzingLabels.asthenope_klachten}
                        onChange={async (event) => {
                            await handleChangeRedenVanVerwijzing('asthenope_klachten', event.target.checked);
                        }}
                    />
                    {props.isOrthOFol && (
                        <Checkbox
                            checked={props.request.customControlData.reden_van_verwijzing.vervolg_onderzoek}
                            label={orthopticsRequestCustomControlDataRedenVanVerwijzingLabels.vervolg_onderzoek}
                            onChange={async (event) => {
                                await handleChangeRedenVanVerwijzing('vervolg_onderzoek', event.target.checked);
                            }}
                        />
                    )}
                </Spacer>
                <div style={{ alignItems: 'center', display: 'flex' }}>
                    <Checkbox
                        checked={isAndersChecked}
                        onChange={() => {
                            setIsAndersChecked(!isAndersChecked);
                        }}
                    />
                    <TextInput
                        onChange={(event) => {
                            setIsAndersChecked(!!event.target.value);
                            handleChangeRedenVanVerwijzing(
                                'andersText',
                                event.target.value ? event.target.value : null
                            );
                        }}
                        placeholder="Anders, namelijk..."
                        style={{ marginLeft: '12px' }}
                        value={props.request.customControlData.reden_van_verwijzing.andersText ?? ''}
                    />
                </div>
                <FormValidationMessage error={redenVanVerwijzingError} />
            </FormGroup>

            <FormGroup
                hasErrors={props.errors.has('customControlData.omschrijf_de_duur_van_de_klachten')}
                label="Duur van de klachten"
            >
                <InputText
                    error={props.errors.get('customControlData.omschrijf_de_duur_van_de_klachten')}
                    nullable
                    onChange={async (value) => {
                        await props.handleChangeCustomControl({ omschrijf_de_duur_van_de_klachten: value });
                        props.validateOneCustomControl('omschrijf_de_duur_van_de_klachten');
                    }}
                    placeholder="Omschrijf de duur van de klachten..."
                    value={props.request.customControlData.omschrijf_de_duur_van_de_klachten}
                />
            </FormGroup>

            <FormGroup
                hasErrors={props.errors.has('customControlData.relevante_medische_voorgeschiedenis')}
                label="Medische voorgeschiedenis"
            >
                <InputText
                    error={props.errors.get('customControlData.relevante_medische_voorgeschiedenis')}
                    nullable
                    onChange={async (value) => {
                        await props.handleChangeCustomControl({ relevante_medische_voorgeschiedenis: value });
                        props.validateOneCustomControl('relevante_medische_voorgeschiedenis');
                    }}
                    placeholder="Omschrijf de relevante medische voorgeschiedenis..."
                    value={props.request.customControlData.relevante_medische_voorgeschiedenis ?? ''}
                />
            </FormGroup>

            <FormGroup
                hasErrors={props.errors.has('customControlData.oculaire_voorgeschiedenis')}
                label="Oculaire voorgeschiedenis"
            >
                <InputText
                    error={props.errors.get('customControlData.oculaire_voorgeschiedenis')}
                    nullable
                    onChange={async (value) => {
                        await props.handleChangeCustomControl({ oculaire_voorgeschiedenis: value });
                        props.validateOneCustomControl('oculaire_voorgeschiedenis');
                    }}
                    placeholder="Omschrijf de oculaire voorgeschiedenis..."
                    value={props.request.customControlData.oculaire_voorgeschiedenis ?? ''}
                />
            </FormGroup>

            <FormGroup
                hasErrors={props.errors.has('customControlData.familiaire_voorgeschiedenis')}
                label="Familiaire voorgeschiedenis"
            >
                <InputText
                    error={props.errors.get('customControlData.familiaire_voorgeschiedenis')}
                    nullable
                    onChange={async (value) => {
                        await props.handleChangeCustomControl({ familiaire_voorgeschiedenis: value });
                        props.validateOneCustomControl('familiaire_voorgeschiedenis');
                    }}
                    placeholder="Omschrijf de familiaire voorgeschiedenis..."
                    value={props.request.customControlData.familiaire_voorgeschiedenis ?? ''}
                />
            </FormGroup>

            <FormGroup
                hasErrors={props.errors.has('customControlData.toelichting')}
                label="Toelichting"
            >
                <InputText
                    error={props.errors.get('customControlData.toelichting')}
                    nullable
                    onChange={async (value) => {
                        await props.handleChangeCustomControl({ toelichting: value });
                        props.validateOneCustomControl('toelichting');
                    }}
                    placeholder="Een toelichting, opmerkingen of vragen..."
                    value={props.request.customControlData.toelichting ?? ''}
                />
            </FormGroup>

            {props.isOrthOFol ? <OrthOFolV1FollowingSteps /> : <OrthOV1FollowingSteps />}

            <ButtonContainer
                alignRight
                buttons={[
                    <CancelButton
                        cancel={props.onCancel}
                        entity={ProcessEntity.request}
                        isExisting={!!props.request.id}
                        key={'cancelButton'}
                        revertChanges={props.revertRequestChanges}
                        serviceCode={props.service.code}
                        serviceType={props.service.serviceType}
                    />,
                    <SaveButton
                        buttonDisabled={!!isSaving || isUploading}
                        key={'saveButton'}
                        save={async () => setIsSaving('draft')}
                    />,
                    <SaveSendButton
                        buttonDisabled={!!isSaving || isUploading}
                        key={'saveSendButton'}
                        saveSend={async () => setIsSaving('final')}
                    />,
                ]}
            />
        </Form>
    );

    async function saveRequest(saveType: 'draft' | 'final') {
        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest || isAndersInvalid) {
            setIsSaving(false);

            return;
        }

        if (
            orthoPermissions?.canAddAssets &&
            saveType === 'final' &&
            (!props.request.customControlData.files || props.request.customControlData.files.length === 0)
        ) {
            setFileUploadErrorMessage(translateKey('dropzone.addReferralLetter'));
            setIsSaving(false);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);

        await props.onSave(requestId);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
    }

    async function handleChange<K extends keyof IRequestAddEdit>(name: K, value: IRequestAddEdit[K]) {
        await props.handleChange(name, value);
    }

    async function handleChangeRedenVanVerwijzing<
        K extends keyof IOrthopticsRequestCustomControlDataRedenVanVerwijzing,
    >(name: K, value: IOrthopticsRequestCustomControlDataRedenVanVerwijzing[K], doValidate = true) {
        await props.handleChangeCustomControl({
            reden_van_verwijzing: {
                ...props.request.customControlData.reden_van_verwijzing,
                [name]: value,
            },
        });

        if (doValidate) {
            props.validateOneCustomControl('reden_van_verwijzing');
        }
    }
}
